import { Badge } from "@/components/ui/badge";

/* Shared "not available yet" primitive (design-port: developer-settings lane 1).
   Extracted verbatim from the local `UnavailableValue` / `UnavailablePanel`
   helpers in `features/developer/developer-home.tsx`, which `features/settings/
   settings.tsx` re-implemented a third time as `NOT_AVAILABLE_FIELDS`.

   Why this exists as a primitive rather than copy per page: the product rule is
   that a field with no backend endpoint renders an explicit "not available yet"
   marker, never a fabricated value. When each page writes its own marker the
   wording and the styling drift, and a page can quietly start rendering
   something else. One primitive keeps the honest state identical everywhere.

   Both exports render exactly the copy and markup the pages used before, so
   moving to this module is behaviour- and copy-preserving. */

/**
 * Inline marker for a single field/value the backend does not carry.
 *
 * `label` lets a cell name the specific absence in its own words ("Not priced")
 * while keeping one marker component, its styling and its test id. The default
 * keeps the original copy, so every existing caller is unchanged.
 */
export function UnavailableValue({ label = "Not available yet" }: { label?: string } = {}) {
  return (
    <span className="font-medium text-muted" data-testid="unavailable-value">
      {label}
    </span>
  );
}

/**
 * Section-sized marker for a whole panel (a table, a log, an aggregate) the
 * backend does not expose. `title` names the surface; `detail` states what is
 * missing in the product's own words — never a placeholder value.
 */
export function UnavailablePanel({ title, detail }: { title: string; detail: string }) {
  return (
    <section className="rounded-xl border border-line bg-surface p-5" data-testid="unavailable-panel">
      <h2 className="mb-3 text-sm font-bold text-ink">{title}</h2>
      <Badge variant="neutral" size="sm">Not available yet</Badge>
      <p className="mt-3 text-sm leading-relaxed text-muted">{detail}</p>
    </section>
  );
}
